// The migration track under the timeline diagram: one tick per migration, sized by how much it
// changed. Click or drag to move, or use the arrow keys.
import { Pause, Play, StepBack, StepForward } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { cn } from '../lib/cn';
import { migrationDate } from '../views/Explorer';
import { changeCount, type TimelineStep } from './model';

export function stepDate(step: TimelineStep): string {
  return step.timestamp ? migrationDate(step.timestamp) : 'not a migration';
}

function hasCustomCode(step: TimelineStep): boolean {
  return !!step.operations && (step.operations.customCode.length > 0 || step.operations.up.some((op) => op.kind === 'Sql' || op.conditional));
}

function tickTitle(step: TimelineStep): string {
  const parts = [step.name, stepDate(step)];
  if (step.model) parts.push(`${changeCount(step)} change${changeCount(step) === 1 ? '' : 's'}`);
  else parts.push('model unknown');
  if (hasCustomCode(step)) parts.push('custom SQL or code');
  if (step.drift) parts.push('differs from the last migration');
  return parts.join(' · ');
}

export function Scrubber(props: {
  steps: TimelineStep[];
  index: number;
  onChange: (index: number) => void;
  playing: boolean;
  onTogglePlay: () => void;
  /** Shows the list of changes (narrow screens, where the inspector is not beside the diagram). */
  onShowChanges?: () => void;
}) {
  const { steps, index, onChange, playing, onTogglePlay, onShowChanges } = props;
  const track = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);
  const max = Math.max(1, ...steps.map(changeCount));
  const step = steps[index]!;
  const count = changeCount(step);

  useEffect(() => {
    const tick = track.current?.children[index] as HTMLElement | undefined;
    tick?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [index]);

  /** The tick under (or nearest to) a pointer position. */
  const indexAt = (clientX: number) => {
    const ticks = [...track.current!.children] as HTMLElement[];
    let best = 0;
    let distance = Infinity;
    ticks.forEach((tick, i) => {
      const r = tick.getBoundingClientRect();
      const d = clientX < r.left ? r.left - clientX : clientX > r.right ? clientX - r.right : 0;
      if (d < distance) {
        distance = d;
        best = i;
      }
    });
    return best;
  };

  const move = (to: number) => onChange(Math.max(0, Math.min(steps.length - 1, to)));

  return (
    <div className="@container flex h-[68px] flex-none items-center gap-3 border-t border-border bg-panel px-3 max-md:gap-2 max-md:px-2 pointer-coarse:h-[76px]" data-testid="scrubber">
      <div className="flex flex-none items-center gap-0.5">
        <ControlButton label="Previous migration" disabled={index === 0} onClick={() => move(index - 1)}>
          <StepBack size={15} />
        </ControlButton>
        <ControlButton label={playing ? 'Pause' : 'Play through the migrations'} onClick={onTogglePlay} active={playing}>
          {playing ? <Pause size={15} /> : <Play size={15} />}
        </ControlButton>
        <ControlButton label="Next migration" disabled={index === steps.length - 1} onClick={() => move(index + 1)}>
          <StepForward size={15} />
        </ControlButton>
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex min-w-0 items-baseline gap-2 text-[12px]">
          <span className={cn('truncate font-medium', step.drift ? 'text-changed' : 'text-text')} data-testid="step-name">
            {step.name}
          </span>
          <span className="flex-none text-faint @max-[30rem]:hidden">{stepDate(step)}</span>
          <span className="flex-none text-faint @max-[30rem]:hidden">·</span>
          {onShowChanges && step.model ? (
            <button type="button" onClick={onShowChanges} className="flex-none text-accent underline decoration-accent/40 underline-offset-2">
              {count} change{count === 1 ? '' : 's'}
            </button>
          ) : (
            <span className="flex-none text-muted">{step.model ? `${count} change${count === 1 ? '' : 's'}` : 'model unknown'}</span>
          )}
          <span className="ml-auto flex-none text-faint">
            {index + 1} of {steps.length}
          </span>
        </div>
        <div
          ref={track}
          role="slider"
          tabIndex={0}
          aria-label="Migration"
          aria-valuemin={1}
          aria-valuemax={steps.length}
          aria-valuenow={index + 1}
          aria-valuetext={`${step.name}, ${stepDate(step)}`}
          // touch-none: a finger drags along the track instead of scrolling it; the current tick scrolls into view.
          className="relative flex h-7 cursor-pointer touch-none items-end gap-[3px] overflow-x-auto rounded border-b border-border outline-none [scrollbar-width:none] focus-visible:ring-1 focus-visible:ring-accent pointer-coarse:h-9"
          onPointerDown={(e) => {
            dragging.current = true;
            e.currentTarget.setPointerCapture(e.pointerId);
            move(indexAt(e.clientX));
          }}
          onPointerMove={(e) => {
            if (dragging.current) {
              const to = indexAt(e.clientX);
              if (to !== index) move(to);
            }
          }}
          onPointerUp={() => {
            dragging.current = false;
          }}
          onKeyDown={(e) => {
            const to = { ArrowLeft: index - 1, ArrowRight: index + 1, Home: 0, End: steps.length - 1, PageUp: index - 10, PageDown: index + 10 }[e.key];
            if (to !== undefined) {
              e.preventDefault();
              e.stopPropagation();
              move(to);
            }
          }}
        >
          {steps.map((s, i) => {
            const n = changeCount(s);
            const height = s.model ? 5 + 21 * (Math.log2(1 + n) / Math.log2(1 + max)) : 22;
            return (
              <div key={s.id} title={tickTitle(s)} className="relative flex h-full max-w-7 min-w-[6px] flex-1 items-end" data-step={s.id}>
                {hasCustomCode(s) && <span className="absolute top-0 left-1/2 h-1 w-1 -translate-x-1/2 rounded-full bg-changed" />}
                <span
                  className={cn(
                    'block w-full rounded-[2px] transition-colors',
                    !s.model
                      ? 'border border-dashed border-border-strong'
                      : s.drift
                        ? 'bg-changed'
                        : i === index
                          ? 'bg-accent'
                          : i < index
                            ? 'bg-accent/40'
                            : 'bg-border-strong',
                  )}
                  style={{ height }}
                />
                {i === index && <span className="absolute -bottom-0.5 left-0 h-0.5 w-full rounded-full bg-text" />}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function ControlButton(props: { label: string; disabled?: boolean; active?: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      title={props.label}
      aria-label={props.label}
      disabled={props.disabled}
      onClick={props.onClick}
      className={cn(
        'flex h-8 w-8 items-center justify-center rounded-md disabled:opacity-35 pointer-coarse:h-10 pointer-coarse:w-10',
        props.active ? 'bg-accent-soft text-accent' : 'text-muted hover:bg-hover hover:text-text',
      )}
    >
      {props.children}
    </button>
  );
}
